import authApi from "interceptor/authInterceptor";
import { createSlice } from "@reduxjs/toolkit";
import { get } from "lodash";
import { dispatch } from "store";
import { onBoardingStatusUpdate, onboardedCompanyDetails, loggedInUserData } from "./auth";
import { updateSelectedCompany } from "./dashboardSearch";
import { updateStaffData } from "./auth";


interface InitialState {
  loader: boolean;
  connections: [];
  tokenData: [];
  users: [];
  companyData: [];

  // Used to re-fetch Rekon company info on refresh without localStorage
  selectedCompanyIdForDetails: string | null;
  companyInfoCache: any;
  companyInfoLoader: boolean;
}

const initialState: InitialState = {
  loader: false,
  connections: [],
  tokenData: [],
  users: [],
  companyData: [],
  selectedCompanyIdForDetails: null,
  companyInfoCache: null,
  companyInfoLoader: false,
};

const getReckonTenantData = createSlice({

  name: "getReckonTenantData",
  initialState,
  reducers: {
    getTenantData(state, action) {
      let { connections, tokenData, companyData } = action.payload;
      state.connections = connections || initialState.connections;
      state.tokenData = tokenData || initialState.tokenData;
      state.companyData = companyData || initialState.companyData;
    },
    getUserData(state, action) {
      let { users } = action.payload;
      state.users = users || initialState.users;
    },
    isLoading(state, action) {
      state.loader = action.payload;
    },

    setSelectedCompanyIdForDetails(state, action) {
      state.selectedCompanyIdForDetails = action.payload || null;
    },
    setCompanyInfoCache(state, action) {
      state.companyInfoCache = action.payload || null;
    },
    setCompanyInfoLoader(state, action) {
      state.companyInfoLoader = Boolean(action.payload);
    },
  },
});


export const {
  getTenantData,
  getUserData,
  isLoading,
  setSelectedCompanyIdForDetails,
  setCompanyInfoCache,
  setCompanyInfoLoader,
} = getReckonTenantData.actions;


export default getReckonTenantData.reducer;

export function processReckonAccessToken(code: string, isReauth?: boolean) {
  return async () => {
    const response = await authApi.post(`v2/api/reckon/accessToken`, {
      code,
      isReauth,
    });
    if (response.data) {
      const reckonData = response.data.data;
      const companyData = get(reckonData, "companyFiles.list", []);

      dispatch(
        getTenantData({
          connections: get(reckonData, "connections", []),
          tokenData: get(reckonData, "accessToken", "")
            ? {
                access_token: get(reckonData, "accessToken"),
                refresh_token: get(reckonData, "refreshToken"),
                expires_in: get(reckonData ,"expiresIn"),
              }
            : [],
          companyData,
        })
      );
    }

    if (!response?.data?.data)
      throw new Error("Something went wrong, please try again");

    return response?.data?.data;
  };
}

export function processReckonCompanyDetails(accessToken: string, companyFileId: string) {
  return async () => {
    const response = await authApi.post(`v2/api/reckon/companyInfo`, {
      accessToken,
      companyFileId,
    });
    if (!response?.data?.data)
      throw new Error("Something went wrong, please try again");

    return response?.data?.data;
  };
}

export function storeReckonCompanyDetails(data: any) {
  return async () => {
    const payload = {
      accessToken: data.accessToken ?? data?.tokenData?.access_token,
      refreshToken: data.refreshToken ?? data?.tokenData?.refresh_token,
      expiresIn: data.expiresIn ?? data?.tokenData?.expiresIn ?? data?.tokenData?.expires_in,
      companyFileId: data.companyFileId,
      companyName: data.companyName,
      companyEmail: data.email,
      companyPhone: data.phone,
      companyAbn: data.abn,
      deductionMappings: data.deductionMappings,
      companyId: data.companyId,
    };

    const response = await authApi.post(`/v2/api/reckon/saveDetails`, payload);

    if (data.isReauth) {
      return response?.data?.data || response?.data;
    }

    const companyData = response?.data?.data?.company || response?.data?.data?.companyData;
    const companyId = response?.data?.data?.companyId || response?.data?.data?.company?._id;

    // Dashboard guard requires auth.onBoardedCompanyData._id, so we must construct it.
    const resolvedCompanyData =
      companyData ||
      (companyId
        ? {
            _id: companyId,
            company_name:
              (response?.data?.data?.companyName as string | undefined) ??
              data.companyName,
            companyName:
              (response?.data?.data?.companyName as string | undefined) ??
              data.companyName,
          }
        : null);

    const onBoardedUserCompanyDetails = {
      company_name:
        resolvedCompanyData?.company_name ||
        resolvedCompanyData?.companyName ||
        data.companyName ||
        "",
      _id: resolvedCompanyData?._id || companyId || "",
    };

    if (resolvedCompanyData) {
      dispatch(
        onBoardingStatusUpdate({
          onBoardedCompanyData: resolvedCompanyData,
        })
      );
      dispatch(
        onboardedCompanyDetails({
          onBoardedCompanyDetails: onBoardedUserCompanyDetails,
        })
      );
    }


    // Ensure staff/user/company linkage reflects in dashboard selection.
    dispatch(updateSelectedCompany({ selectedCompany: onBoardedUserCompanyDetails }));
    dispatch(updateStaffData({ staffData: null }));
    dispatch(
      loggedInUserData({
        onBoardedCompanyDetails: onBoardedUserCompanyDetails,
        isCashDSuperAdmin: false,
        isEmployerAdmin: false,
        isManagerAdmin: false,
        isPayrollOfficer: false,
      })
    );

    return response?.data?.data || response?.data;
  };
}

export function fetchReckonDeductionItems(companyId: string) {
  return async () => {
    const response = await authApi.post(`/v2/api/reckon/fetchReckonDeductionItems`, { companyId });
    if (!response?.data) {
      throw new Error("Failed to fetch deduction items.");
    }
    return response.data.data;
  };
}

export function updateDeductionMappings(companyId: string, deductionMappings: any) {
  return async () => {
    const response = await authApi.post(`/v2/api/reckon/updateDeductionMappings`, {
      companyId,
      deductionMappings,
    });
    return response.data;
  };
}

